import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { cva, type VariantProps } from 'class-variance-authority';
import { useIntl, defineMessages } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { cn } from 'src/utils/shadcn';

const messages = defineMessages({
    somethingWentWrong: {
        id: 'something-went-wrong',
        defaultMessage: 'Something went wrong',
    },
    somethingWentWrongRetry: {
        id: 'something-went-wrong-retry',
        defaultMessage: 'Please retry again later or contact an administrator',
    },
});

const generalMessageBlockVariants = cva(
    "relative flex w-full items-center justify-center overflow-hidden bg-[url('/pattern.png')] bg-center bg-no-repeat py-36 text-center",
    {
        variants: {
            variant: {
                error: 'text-red-500',
                warn: 'text-orange-500',
            },
        },
    },
);

type GeneralMessageBlockProps = {
    title?: string;
    subtitle?: string;
    icon?: IconProp;
    className?: string;
    children?: React.ReactNode;
};

const GeneralMessageBlock = ({
    title,
    icon,
    subtitle,
    variant,
    className,
    children,
}: GeneralMessageBlockProps & VariantProps<typeof generalMessageBlockVariants>) => {
    const { formatMessage } = useIntl();

    return (
        <div className={cn(generalMessageBlockVariants({ variant }), className)}>
            {children ? (
                children
            ) : (
                <div className="relative flex max-w-xs flex-col items-center gap-1 text-balance">
                    {icon && <SquareIcon icon={icon} variant={variant} />}
                    <h3 className={cn('mt-4 text-lg font-semibold')}>
                        {title || formatMessage(messages.somethingWentWrong)}
                    </h3>
                    <p className={cn('whitespace-pre-line text-sm opacity-80')}>
                        {subtitle || formatMessage(messages.somethingWentWrongRetry)}
                    </p>
                </div>
            )}
        </div>
    );
};

export default GeneralMessageBlock;
